/**
 * ApplicationsPage.tsx — Applications & Integration View.
 *
 * Lists applications via GET /v1/applications, displays consent counts per app,
 * includes a create new application modal (POST /v1/applications) with form validation
 * and environment selector (Production, Staging, Dev), application secret reveal/copy,
 * search filter, loading skeletons, empty state, and toast notifications.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useToast } from '../../components/ui/Toast';
import { SkeletonCard } from '../../components/ui/Skeleton';
import { EmptyState } from '../../components/ui/EmptyState';
import { Spinner } from '../../components/ui/Spinner';
import { apiFetch, getNetworkErrorMessage, setApiKey } from '../../lib/api';
import type { Application, ConsentRecord } from '../../lib/types';
import { STORAGE_KEYS } from '../../lib/constants';

interface AppSuccessDetails {
  id: string;
  name: string;
  apiKey: string;
  environment: string;
  date: string;
}

export function ApplicationsPage() {
  const { addToast } = useToast();

  const [applications, setApplications] = useState<Application[]>([]);
  const [consents, setConsents] = useState<ConsentRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Search & Filter
  const [searchQuery, setSearchQuery] = useState('');

  // Create App Modal state
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [newAppName, setNewAppName] = useState('');
  const [environment, setEnvironment] = useState<'production' | 'staging' | 'dev'>('production');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Success secret modal state
  const [successDetails, setSuccessDetails] = useState<AppSuccessDetails | null>(null);
  const [showSecret, setShowSecret] = useState(false);
  const [copiedKey, setCopiedKey] = useState(false);
  const [copiedAppId, setCopiedAppId] = useState(false);

  // Active App ID from localStorage
  const activeAppId = localStorage.getItem(STORAGE_KEYS.APP_ID) || '';

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [appsRes, consentsRes] = await Promise.allSettled([
        apiFetch<{ applications: Application[] }>('/v1/applications', { useApiKey: true }),
        apiFetch<{ consents: ConsentRecord[] }>('/v1/consents', { useApiKey: true }),
      ]);

      if (appsRes.status === 'fulfilled') {
        setApplications(appsRes.value.applications || []);
      }
      if (consentsRes.status === 'fulfilled') {
        setConsents(consentsRes.value.consents || []);
      }
    } catch (err) {
      setError(getNetworkErrorMessage(err, 'Failed to load applications.'));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Calculate consent count per app
  const getAppConsentCount = (_appId: string) => {
    return consents.length; // In single-app environment or filtered
  };

  // Create Application submission
  const handleCreateAppSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newAppName.trim()) {
      addToast({ type: 'error', message: 'Application name is required.' });
      return;
    }

    setIsSubmitting(true);
    try {
      const data = await apiFetch<{ applicationId: string; name: string; apiKey: string }>(
        '/v1/applications',
        {
          method: 'POST',
          body: { name: newAppName.trim() },
          useApiKey: true,
        },
      );

      setNewAppName('');
      setIsCreateModalOpen(false);
      await fetchData();

      setSuccessDetails({
        id: data.applicationId,
        name: data.name,
        apiKey: data.apiKey,
        environment,
        date: new Date().toLocaleDateString(),
      });

      addToast({ type: 'success', message: `Application "${data.name}" created!` });
    } catch (err) {
      addToast({
        type: 'error',
        message: getNetworkErrorMessage(err, 'Failed to create application.'),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Switch Active Application Context
  const handleSwitchApp = (app: Application) => {
    localStorage.setItem(STORAGE_KEYS.APP_ID, app.id);
    addToast({ type: 'success', message: `Switched active context to "${app.name}"` });
    fetchData();
  };

  // Filtered applications list
  const filteredApplications = applications.filter(
    (app) =>
      app.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      app.id.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  return (
    <div className="space-y-8">
      {/* Header & Create Button */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-ink">
            Applications & Integration
          </h1>
          <p className="mt-1 text-sm text-sub">
            Manage your registered applications, credentials, and environment settings.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setIsCreateModalOpen(true)}
          className="btn-p text-sm font-semibold py-2.5 px-5 cursor-pointer shadow-md"
        >
          + Register New Application
        </button>
      </div>

      {/* Search & Stats Bar */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4 rounded-2xl border border-line bg-surface p-4 shadow-card">
        <div className="relative flex-1 max-w-md">
          <input
            type="text"
            placeholder="Search applications by name or ID..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full rounded-xl border border-line bg-card px-4 py-2.5 pl-10 text-xs text-ink placeholder-muted focus:border-blue-500 outline-none transition"
          />
          <span className="absolute left-3.5 top-3 text-muted text-xs">🔍</span>
        </div>
        <div className="flex items-center gap-4 text-xs text-sub">
          <span>Total Applications: <strong className="text-ink font-bold">{applications.length}</strong></span>
          {activeAppId && (
            <span className="hidden md:inline-flex items-center gap-1.5 rounded-full border border-blue-500/20 bg-blue-500/10 px-3 py-1 text-blue-600 dark:text-blue-400 font-medium">
              Active Context: <code className="mono font-bold">{activeAppId.substring(0, 12)}...</code>
            </span>
          )}
        </div>
      </div>

      {/* Error Banner */}
      {error && (
        <div className="rounded-xl border border-err/30 bg-err/10 p-4 flex items-center justify-between text-sm text-err">
          <span>⚠️ {error}</span>
          <button type="button" onClick={fetchData} className="text-xs font-bold underline hover:no-underline ml-4">
            Retry
          </button>
        </div>
      )}

      {/* Application Cards List */}
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <SkeletonCard />
          <SkeletonCard />
          <SkeletonCard />
        </div>
      ) : filteredApplications.length === 0 ? (
        <EmptyState
          icon="📱"
          title={searchQuery ? 'No Matching Applications' : 'No Applications Registered'}
          description={
            searchQuery
              ? `No application matches "${searchQuery}". Try a different search phrase.`
              : 'Register your first client application to start managing consents and generating API keys.'
          }
          action={
            <button
              type="button"
              onClick={() => setIsCreateModalOpen(true)}
              className="btn-p text-xs py-2 px-4 cursor-pointer"
            >
              + Create Application
            </button>
          }
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredApplications.map((app) => {
            const isActive = app.id === activeAppId;
            const consentCount = getAppConsentCount(app.id);

            return (
              <div
                key={app.id}
                className={`rounded-2xl border ${
                  isActive ? 'border-blue-500/50 bg-blue-500/5 shadow-premium' : 'border-line bg-surface'
                } p-6 shadow-card hover:shadow-cardhover transition-all flex flex-col justify-between space-y-5`}
              >
                <div>
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="text-base font-bold text-ink truncate" title={app.name}>
                      {app.name}
                    </h3>
                    <span
                      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${
                        isActive
                          ? 'border border-blue-500/30 bg-blue-500/10 text-blue-600 dark:text-blue-400'
                          : 'border border-line bg-card text-muted'
                      }`}
                    >
                      {isActive ? 'Active' : 'Registered'}
                    </span>
                  </div>
                  <p className="mt-1 font-mono text-[11px] text-muted truncate" title={app.id}>
                    {app.id}
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-2 rounded-xl border border-line bg-card p-3 text-xs">
                  <div>
                    <span className="text-muted block text-[10px] uppercase font-bold">Consents</span>
                    <span className="text-ink font-extrabold mt-0.5 block">{consentCount}</span>
                  </div>
                  <div>
                    <span className="text-muted block text-[10px] uppercase font-bold">Created</span>
                    <span className="text-sub font-medium mt-0.5 block">
                      {new Date((app.created_at || Date.now() / 1000) * 1000).toLocaleDateString()}
                    </span>
                  </div>
                </div>

                <div className="flex items-center justify-between gap-2 pt-2 border-t border-line">
                  <button
                    type="button"
                    onClick={() => {
                      navigator.clipboard.writeText(app.id);
                      addToast({ type: 'success', message: 'Application ID copied to clipboard!' });
                    }}
                    className="rounded-lg border border-line bg-card px-3 py-1.5 text-xs font-semibold text-sub hover:text-ink hover:border-slate-400 transition"
                  >
                    Copy ID
                  </button>
                  <button
                    type="button"
                    onClick={() => handleSwitchApp(app)}
                    className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition ${
                      isActive
                        ? 'border border-blue-500/30 bg-blue-500/10 text-blue-600 dark:text-blue-400'
                        : 'btn-g py-1.5'
                    }`}
                  >
                    {isActive ? 'Selected' : 'Select Context'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Create Application Modal */}
      {isCreateModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 backdrop-blur-sm p-4 overflow-y-auto">
          <div className="w-full max-w-md rounded-2xl border border-line bg-card p-6 shadow-cardhover space-y-6">
            <div className="flex items-center justify-between border-b border-line pb-4">
              <h3 className="text-lg font-bold text-ink flex items-center gap-2">
                <span>📱</span> Create Application
              </h3>
              <button
                type="button"
                onClick={() => setIsCreateModalOpen(false)}
                className="text-sub hover:text-ink text-sm font-bold p-1"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleCreateAppSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-muted mb-1.5">
                  Application Name *
                </label>
                <input
                  type="text"
                  placeholder="e.g. Customer Web Portal"
                  value={newAppName}
                  onChange={(e) => setNewAppName(e.target.value)}
                  className="w-full rounded-xl border border-line bg-surface px-4 py-2.5 text-sm text-ink placeholder-muted focus:border-blue-500 outline-none"
                  disabled={isSubmitting}
                  maxLength={80}
                  required
                />
              </div>

              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-muted mb-1.5">
                  Target Environment *
                </label>
                <select
                  value={environment}
                  onChange={(e) => setEnvironment(e.target.value as 'production' | 'staging' | 'dev')}
                  className="w-full rounded-xl border border-line bg-surface px-4 py-2.5 text-sm text-ink focus:border-blue-500 outline-none cursor-pointer"
                  disabled={isSubmitting}
                >
                  <option value="production">Production (dpdp_live_...)</option>
                  <option value="staging">Staging (dpdp_stage_...)</option>
                  <option value="dev">Development (dpdp_test_...)</option>
                </select>
              </div>

              <div className="pt-4 flex items-center justify-end gap-3 border-t border-line">
                <button
                  type="button"
                  onClick={() => setIsCreateModalOpen(false)}
                  className="btn-g text-xs py-2.5 px-4"
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting || !newAppName.trim()}
                  className="btn-p text-xs py-2.5 px-5 disabled:opacity-50 flex items-center gap-2"
                >
                  {isSubmitting ? (
                    <>
                      <Spinner size="sm" /> Creating...
                    </>
                  ) : (
                    'Create Application'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Secret Reveal Overlay Modal */}
      {successDetails && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/70 backdrop-blur-sm p-4 overflow-y-auto">
          <div className="w-full max-w-lg rounded-2xl border border-line bg-card p-6 sm:p-8 shadow-cardhover space-y-6">
            <div className="flex items-center gap-3">
              <span className="text-3xl">🎉</span>
              <div>
                <h3 className="text-xl font-bold text-ink">Application Registered</h3>
                <p className="text-xs text-sub mt-0.5">
                  Store these generated credentials securely.
                </p>
              </div>
            </div>

            <div className="rounded-xl border border-warn/30 bg-warn/10 p-4 text-xs text-warn leading-relaxed space-y-1">
              <span className="font-bold block">⚠️ Security Warning</span>
              <p>
                This API Key will only be displayed once. Please copy and store it safely in your environment variables.
              </p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-wider text-muted">
                  Application Name
                </label>
                <p className="mt-1 text-sm font-semibold text-ink">{successDetails.name}</p>
              </div>

              <div>
                <label className="block text-[10px] font-bold uppercase tracking-wider text-muted">
                  Application ID
                </label>
                <div className="mt-1 flex items-center justify-between rounded-xl border border-line bg-surface p-3">
                  <span className="font-mono text-xs text-ink truncate pr-3">{successDetails.id}</span>
                  <button
                    type="button"
                    onClick={() => {
                      navigator.clipboard.writeText(successDetails.id);
                      setCopiedAppId(true);
                      setTimeout(() => setCopiedAppId(false), 2000);
                    }}
                    className="text-xs font-bold text-blue-600 hover:text-blue-500"
                  >
                    {copiedAppId ? 'Copied!' : 'Copy'}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-[10px] font-bold uppercase tracking-wider text-muted">
                  API Key Secret
                </label>
                <div className="mt-1 flex items-center justify-between rounded-xl border border-line bg-surface p-3">
                  <span className="font-mono text-xs text-ink truncate pr-3">
                    {showSecret ? successDetails.apiKey : '••••••••••••••••••••••••••••••••••••••••'}
                  </span>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setShowSecret(!showSecret)}
                      className="text-xs text-muted hover:text-ink px-2"
                    >
                      {showSecret ? 'Hide' : 'Reveal'}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        navigator.clipboard.writeText(successDetails.apiKey);
                        setCopiedKey(true);
                        setTimeout(() => setCopiedKey(false), 2000);
                      }}
                      className="text-xs font-bold text-blue-600 hover:text-blue-500"
                    >
                      {copiedKey ? 'Copied!' : 'Copy Key'}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="pt-4 border-t border-line flex flex-col sm:flex-row gap-3">
              <button
                type="button"
                onClick={() => {
                  setApiKey(successDetails.apiKey, successDetails.id);
                  setSuccessDetails(null);
                  addToast({ type: 'success', message: `Active context set to "${successDetails.name}"` });
                  fetchData();
                }}
                className="btn-p flex-1 py-2.5 text-xs justify-center"
              >
                Set as Active Application
              </button>
              <button
                type="button"
                onClick={() => setSuccessDetails(null)}
                className="btn-g flex-1 py-2.5 text-xs justify-center"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
